import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, formatDate } from '@/lib/money';
import type { Contract, ContractListResponse } from './types';
import { FREQUENCIES, STATUS_STYLE } from './types';
import { ContractForm } from './ContractForm';
import { ContractPanel, ActionDialog } from './ContractPanel';

/**
 * Contratos — RF-CON.
 *
 * A tela precisa deixar claro, logo de saída, que contrato NÃO é um lugar
 * separado de dinheiro: os títulos que ele gera entram nas receitas e despesas
 * como qualquer outro lançamento. Sem isso, é natural supor que existe uma
 * contabilidade paralela — e desconfiar dos totais do resto do sistema.
 */
export function ContractsPage({ selectedIds }: { selectedIds: number[] }) {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Contract | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [terminating, setTerminating] = useState<Contract | null>(null);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ['contracts', selectedIds],
    queryFn: () => api.get<ContractListResponse>('/contracts', { entityIds: selectedIds }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['contracts'] });
    void queryClient.invalidateQueries({ queryKey: ['receivables'] });
    void queryClient.invalidateQueries({ queryKey: ['payables'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const bill = useMutation({
    mutationFn: (id: number) =>
      api.post<{ meta: { note: string } }>(`/contracts/${id}/bill`, {}, { entityIds: selectedIds }),
    onSuccess: (res) => {
      invalidate();
      alert(res.meta.note);
    },
    onError: (e) => alert(e instanceof ApiError ? e.message : 'Falha ao faturar.'),
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(`/contracts/${id}`, { entityIds: selectedIds }),
    onSuccess: (res) => {
      invalidate();
      alert(res.message);
    },
    onError: (e) => alert(e instanceof ApiError ? e.message : 'Falha ao excluir.'),
  });

  const contracts = list.data?.data ?? [];
  const meta = list.data?.meta;

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Contratos</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
            Os títulos gerados entram em <strong>A receber</strong> e <strong>A pagar</strong> como
            qualquer lançamento — o contrato guarda o que o lançamento não sabe: vigência, renovação,
            reajuste e as partes.
          </p>
        </div>

        <button
          onClick={() => setCreating(true)}
          className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
        >
          + Novo contrato
        </button>
      </div>

      {/* ---------- Carteira ---------- */}
      {meta && meta.active_count > 0 && (
        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kpi label="Receita mensal contratada" value={meta.monthly_recurring_income} tone="receita" />
          <Kpi label="Despesa mensal contratada" value={meta.monthly_recurring_expense} tone="despesa" />
          <Counter
            label="Vencendo"
            value={meta.expiring_soon}
            hint="dentro do aviso prévio"
            alert={meta.expiring_soon > 0}
          />
          <Counter
            label="Reajuste pendente"
            value={meta.due_for_adjustment}
            hint="chegou a data"
            alert={meta.due_for_adjustment > 0}
          />
        </div>
      )}

      {list.isPending ? (
        <div className="mt-5 animate-pulse space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-slate-100 dark:bg-slate-800" />
          ))}
        </div>
      ) : contracts.length === 0 ? (
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-10 text-center dark:border-slate-800 dark:bg-slate-900">
          <p className="text-sm text-slate-500 dark:text-slate-400">Nenhum contrato cadastrado.</p>
          {/* O vazio explica quando vale a pena usar — e quando não vale. */}
          <p className="mx-auto mt-2 max-w-lg text-xs text-slate-400">
            Vale cadastrar aqui o acordo que tem <strong>prazo</strong>, <strong>renovação</strong> ou{' '}
            <strong>reajuste</strong>. Recebimento mensal informal, sem nada disso, fica melhor em
            Recorrências — aqui viraria cadastro duplicado.
          </p>
        </div>
      ) : (
        <div className="mt-5 space-y-2">
          {contracts.map((c) => (
            <ContractRow
              key={c.id}
              contract={c}
              open={openId === c.id}
              onToggle={() => setOpenId(openId === c.id ? null : c.id)}
              onEdit={() => setEditing(c)}
              onBill={() => bill.mutate(c.id)}
              billing={bill.isPending}
              onDelete={() => {
                if (
                  confirm(
                    `Excluir o contrato "${c.title}"? Ele nunca gerou títulos, então nada de financeiro é perdido.`,
                  )
                ) {
                  remove.mutate(c.id);
                }
              }}
              onTerminate={() => setTerminating(c)}
            />
          ))}
        </div>
      )}

      {openId !== null && (
        <ContractPanel contractId={openId} selectedIds={selectedIds} onChanged={invalidate} />
      )}

      {terminating && (
        <ActionDialog
          kind="terminate"
          contract={terminating}
          selectedIds={selectedIds}
          onClose={() => setTerminating(null)}
          onDone={() => {
            setTerminating(null);
            invalidate();
          }}
        />
      )}

      {(creating || editing) && (
        <ContractForm
          contract={editing}
          selectedIds={selectedIds}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(note) => {
            setCreating(false);
            setEditing(null);
            invalidate();
            // O servidor diz quantos títulos nasceram e quantos já venceram.
            // Sem isso, encontrar dois em atraso na tela seguinte parece defeito.
            if (note) alert(note);
          }}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------

function ContractRow({
  contract,
  open,
  onToggle,
  onEdit,
  onBill,
  billing,
  onDelete,
  onTerminate,
}: {
  contract: Contract;
  open: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onBill: () => void;
  billing: boolean;
  onDelete: () => void;
  onTerminate: () => void;
}) {
  const style = STATUS_STYLE[contract.status] ?? STATUS_STYLE.rascunho!;
  const frequency = FREQUENCIES.find((f) => f.id === contract.frequency)?.label ?? contract.frequency;

  return (
    <div
      className={`rounded-xl border bg-white p-3.5 transition dark:bg-slate-900 ${
        open
          ? 'border-indigo-300 dark:border-indigo-800'
          : 'border-slate-200 dark:border-slate-800'
      }`}
    >
      <div className="flex flex-wrap items-start gap-3">
        <button onClick={onToggle} className="min-w-0 flex-1 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{contract.title}</span>
            {contract.number && (
              <span className="text-xs text-slate-400">nº {contract.number}</span>
            )}
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${style.chip}`}>
              {style.label}
            </span>
            <span
              className={`text-[10px] font-medium uppercase ${
                contract.direction === 'receita'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-rose-600 dark:text-rose-400'
              }`}
            >
              {contract.direction === 'receita' ? '↓ recebo' : '↑ pago'}
            </span>
          </div>

          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {formatMoney(contract.amount)} · {frequency}
            {contract.due_day && ` · vence dia ${contract.due_day}`}
            {contract.primary_party_name && ` · ${contract.primary_party_name}`}
          </p>

          <p className="mt-0.5 text-xs text-slate-400">
            <Vigencia contract={contract} />
            {contract.entity_name && ` · ${contract.entity_name}`}
          </p>
        </button>

        <div className="flex shrink-0 items-center gap-2 text-xs">
          {contract.status === 'ativo' && (
            <button
              onClick={onBill}
              disabled={billing}
              className="rounded-lg border border-slate-300 px-2.5 py-1.5 transition hover:border-indigo-500 hover:text-indigo-600 disabled:opacity-50 dark:border-slate-700"
              /*
                O que o botão É, em uma frase.

                O faturamento normal é automático: acontece ao cadastrar e todo
                dia na rotina das 06:30. Este botão é a saída de emergência —
                serve quando a rotina não rodou, quando o contrato saiu de
                rascunho, ou para forçar a geração agora. Sem dizer isso, ele
                parece um passo obrigatório que ninguém entende.
              */
              title={
                'O faturamento já é automático — ao cadastrar e todo dia às 06:30. ' +
                'Use este botão para forçar agora: se a rotina não rodou, ou se o contrato ' +
                'acabou de sair de rascunho. Pode repetir à vontade: o mesmo vencimento ' +
                'nunca é gerado duas vezes.'
              }
            >
              Faturar
            </button>
          )}
          <button onClick={onEdit} className="text-slate-500 dark:text-slate-400 hover:text-indigo-600">
            Editar
          </button>

          {/* ================================================================
              EXCLUIR *OU* ENCERRAR — nunca os dois
              ================================================================

              Contrato que já gerou título não pode ser excluído: os títulos
              ficariam apontando para um contrato que sumiu, e "de onde veio
              esta receita?" perderia a resposta. O caminho ali é encerrar.

              Mostrar os dois e recusar um deles ensinaria o usuário a ignorar
              mensagem de erro. */}
          {contract.can_delete ? (
            <button onClick={onDelete} className="text-slate-500 dark:text-slate-400 hover:text-rose-600">
              Excluir
            </button>
          ) : (
            !['encerrado', 'cancelado'].includes(contract.status) && (
              <button
                onClick={onTerminate}
                className="text-slate-500 dark:text-slate-400 hover:text-rose-600"
                title={`Já gerou ${contract.billed_count} título(s) — por isso encerra, não exclui`}
              >
                Encerrar
              </button>
            )
          )}
        </div>
      </div>

      {/* Os dois avisos que só o contrato sabe dar. */}
      {contract.in_notice_window && (
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
          {contract.auto_renew
            ? `Renova automaticamente por ${contract.renewal_months} meses se ninguém avisar até ${formatDate(contract.ends_at!)}.`
            : `Vence em ${formatDate(contract.ends_at!)} e não renova sozinho — sem renovação, o faturamento para.`}
        </p>
      )}

      {contract.due_for_adjustment && (
        <p className="mt-2 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:bg-sky-950/30 dark:text-sky-300">
          Reajuste previsto para {formatDate(contract.next_adjustment_at!)}
          {contract.adjustment_index !== 'fixo'
            ? ` — consulte o ${contract.adjustment_index} acumulado e informe ao aplicar.`
            : ` — ${contract.adjustment_percentage}% previstos em contrato.`}
        </p>
      )}
    </div>
  );
}

function Vigencia({ contract }: { contract: Contract }) {
  if (contract.ends_at === null) {
    return <>desde {formatDate(contract.starts_at)} · prazo indeterminado</>;
  }

  const days = contract.days_to_expiry;

  return (
    <>
      {formatDate(contract.starts_at)} a {formatDate(contract.ends_at)}
      {days !== null && days >= 0 && ` · ${days} dia${days === 1 ? '' : 's'} restante${days === 1 ? '' : 's'}`}
      {days !== null && days < 0 && ' · vencido'}
      {/*
        DOIS números, e não um.

        `total_committed` é o que AINDA será faturado — quando o contrato já
        gerou tudo, ele é zero. Sozinho, sob um rótulo que promete "contratado",
        isso aparecia como "R$ 0,00 contratados" ao lado de "R$ 3.500,00 ·
        Mensal": dois números da mesma linha se contradizendo, e a boa notícia
        (está tudo faturado) lida como contrato sem valor.
      */}
      {Number(contract.billed_total) > 0 &&
        ` · ${formatMoney(contract.billed_total)} já faturados`}

      {contract.total_committed !== null && Number(contract.total_committed) > 0 &&
        ` · ${formatMoney(contract.total_committed)} a faturar`}

      {contract.total_committed !== null &&
        Number(contract.total_committed) === 0 &&
        Number(contract.billed_total) > 0 &&
        ' · tudo já faturado até o fim da vigência'}
    </>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone: 'receita' | 'despesa' }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p
        className={`mt-1 text-lg font-semibold ${
          tone === 'receita'
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-rose-600 dark:text-rose-400'
        }`}
      >
        {formatMoney(value)}
      </p>
      <p className="mt-0.5 text-[11px] text-slate-400">equivalente por mês</p>
    </div>
  );
}

function Counter({
  label,
  value,
  hint,
  alert,
}: {
  label: string;
  value: number;
  hint: string;
  alert: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-3.5 ${
        alert
          ? 'border-amber-300 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/20'
          : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
      }`}
    >
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
      <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p>
    </div>
  );
}
